import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { GeometryMotion, needsImmediateGeometry, type DriverGeometry, type StudioMotionMode } from './raceStudioGeometry';

export function useRaceStudioMotion(root: RefObject<HTMLElement | null>) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const shell = root.current?.closest('[data-motion]');
    const update = () => setEnabled(!media.matches && shell?.getAttribute('data-motion') !== 'off');
    const observer = new MutationObserver(update);
    if (shell) observer.observe(shell, { attributes: true, attributeFilter: ['data-motion'] });
    media.addEventListener('change', update); update();
    return () => { observer.disconnect(); media.removeEventListener('change', update); };
  }, [root]);
  return enabled;
}

export function useAnimatedGeometry(target: DriverGeometry[], enabled: boolean, direct: boolean, layoutKey: string, resetKey?: string | number, mode: StudioMotionMode = 'replay') {
  const engine = useRef<GeometryMotion | null>(null);
  if (!engine.current) engine.current = new GeometryMotion(target, mode);
  const [geometry, setGeometry] = useState(() => engine.current!.current);
  const previousLayout = useRef(layoutKey);
  const previousReset = useRef(resetKey);
  const previousMode = useRef(mode);
  const previousTarget = useRef(target);
  const frame = useRef<number | null>(null);
  useLayoutEffect(() => {
    const animation = engine.current!;
    const layoutChanged = previousLayout.current !== layoutKey;
    const snap = !enabled || direct || previousReset.current !== resetKey || previousMode.current !== mode;
    const immediate = snap || layoutChanged || needsImmediateGeometry(previousTarget.current, target);
    const changed = animation.retarget(target, performance.now(), layoutChanged, snap, mode);
    previousLayout.current = layoutKey;
    previousReset.current = resetKey;
    previousMode.current = mode;
    previousTarget.current = target;
    // Continuous target updates share the scheduled RAF instead of forcing a
    // second synchronous render for every playback-clock frame.
    if (changed && (immediate || !animation.active)) setGeometry(animation.current);
    if (!animation.active && frame.current !== null) { cancelAnimationFrame(frame.current); frame.current = null; }
    if (!animation.active || frame.current !== null) return;
    const tick = (now: number) => {
      frame.current = null;
      setGeometry(animation.sample(now));
      if (animation.active) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }, [target, enabled, direct, layoutKey, resetKey, mode]);
  useLayoutEffect(() => () => { if (frame.current !== null) { cancelAnimationFrame(frame.current); frame.current = null; } }, []);
  return geometry;
}
